import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiServices from '../../services/apiServices';
import './profiles.scss';

const defaultForm = {
  parental_name: '',
  profile_image_url: '',
  parental_pin: '',
};

function Profiles() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(localStorage.getItem('activeProfileId') || '');
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [selectedProfileForPin, setSelectedProfileForPin] = useState(null);
  const [enteredPin, setEnteredPin] = useState('');
  const fileInputRef = useRef(null);

  const getAbsoluteImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/v\d+\.\d+\/?$/, '');
    return `${base}${imagePath}`;
  };

  const syncLocalProfile = (profile) => {
    if (!profile) return;
    localStorage.setItem('activeProfileId', profile.profile_id);
    localStorage.setItem('activeProfile', JSON.stringify(profile));
    setActiveProfileId(profile.profile_id);
  };

  const loadProfiles = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await ApiServices.callGetAPI('profiles');
      if (result.status === 200 && result.data.valid) {
        const list = result.data.data?.profiles || [];
        const activeProfile = result.data.data?.activeProfile || list.find((item) => Number(item.is_active) === 1);
        setProfiles(list);
        if (activeProfile) {
          syncLocalProfile(activeProfile);
        }
      } else {
        setError('Could not load profiles');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Could not load profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }
    setUploadingImage(true);
    setError('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Image read failed'));
        reader.readAsDataURL(file);
      });
      const result = await ApiServices.callPostAPI('profiles/images', {
        image_data: base64,
        mime_type: file.type,
      });
      if (result.status === 200 && result.data.valid) {
        const imagePath = result.data.data?.image_url || '';
        setForm((prev) => ({ ...prev, profile_image_url: imagePath }));
      } else {
        setError(result.data?.error || 'Unable to upload image');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Unable to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const openImagePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSelect = async (profile, pinValue = '') => {
    try {
      const pinCheck = await ApiServices.callPostAPI(`profiles/${profile.profile_id}/verify-pin`, {
        pin: String(pinValue || '').trim(),
      });
      if (!(pinCheck.status === 200 && pinCheck.data.valid && pinCheck.data.data?.valid_pin)) {
        setError('Invalid parental PIN');
        return;
      }
      const result = await ApiServices.callPostAPI('profiles/select', { profile_id: profile.profile_id });
      if (result.status === 200 && result.data.valid) {
        syncLocalProfile(result.data.data?.activeProfile || { ...profile, is_active: 1 });
        localStorage.removeItem('profileSelectionRequired');
        setProfiles((prev) => prev.map((item) => ({
          ...item,
          is_active: item.profile_id === profile.profile_id ? 1 : 0,
        })));
        setPinModalOpen(false);
        setSelectedProfileForPin(null);
        setEnteredPin('');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Unable to select profile');
    }
  };

  const onProfileCardClick = (profile) => {
    setError('');
    if (Number(profile.pin_required) === 1) {
      setSelectedProfileForPin(profile);
      setEnteredPin('');
      setPinModalOpen(true);
      return;
    }
    handleSelect(profile, '');
  };

  const submitPinModal = async () => {
    if (!selectedProfileForPin) return;
    const pin = String(enteredPin || '').trim();
    if (!pin) {
      setError('Parental PIN is required');
      return;
    }
    await handleSelect(selectedProfileForPin, pin);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const profileName = form.parental_name.trim();
    if (!profileName) {
      setError('Name is required');
      return;
    }
    if (!form.parental_pin || !String(form.parental_pin).trim()) {
      setError('Parental PIN is required');
      return;
    }
    if (profiles.length >= 4) {
      setError('Maximum 4 profiles allowed');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        profile_name: profileName,
        parental_name: profileName,
        profile_image_url: form.profile_image_url?.trim() || undefined,
        avatar_url: form.profile_image_url?.trim() || undefined,
        is_kids: false,
        max_age_rating: '18+',
        pin: form.parental_pin || undefined,
        parental_pin: form.parental_pin || undefined,
      };
      const result = await ApiServices.callPostAPI('profiles', payload);
      if (result.status === 200 && result.data.valid) {
        setForm(defaultForm);
        await loadProfiles();
      } else {
        setError(result.data?.error || 'Unable to create profile');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Unable to create profile');
    } finally {
      setSaving(false);
    }
  };

  const goHome = () => {
    if (!activeProfileId) {
      setError('Please select a profile');
      return;
    }
    navigate('/', { replace: true });
  };

  return (
    <div className="profiles-page">
      <div className="profiles-wrap">
        <h1>Who&apos;s watching?</h1>
        <p className="sub">Create up to 4 viewer profiles.</p>

        {error ? <p className="error">{error}</p> : null}

        {loading ? (
          <div className="state-text">Loading profiles...</div>
        ) : (
          <>
            <h2 className="top-title">Existing Profiles</h2>
            <div className="profiles-grid">
              {profiles.map((profile) => (
                <div
                  key={profile.profile_id}
                  className={`profile-card ${activeProfileId === profile.profile_id ? 'active' : ''}`}
                  onClick={() => onProfileCardClick(profile)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onProfileCardClick(profile);
                    }
                  }}
                >
                  {profile.avatar_url ? (
                    <img
                      src={getAbsoluteImageUrl(profile.avatar_url)}
                      alt={profile.profile_name}
                      className="avatar avatar-img"
                    />
                  ) : (
                    <div className="avatar">{(profile.profile_name || 'P').charAt(0).toUpperCase()}</div>
                  )}
                  <div className="name">{profile.profile_name}</div>
                  <div className="meta">Standard | Max {profile.max_age_rating}</div>
                  {activeProfileId === profile.profile_id ? <div className="meta">Active</div> : null}
                </div>
              ))}
            </div>
          </>
        )}

        {profiles.length >= 4 ? (
          <div className="profile-form">
            <h2>Add profile (4/4)</h2>
            <p className="hint">Maximum 4 profiles created. Delete one profile to add a new one.</p>
            <div className="form-row actions">
              <button type="button" className="secondary" onClick={goHome}>
                Continue
              </button>
            </div>
          </div>
        ) : (
          <form className="profile-form" onSubmit={handleCreate}>
            <h2>Add profile ({profiles.length}/4)</h2>
            <div className="form-row">
              <label className="upload-label">Profile image</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="hidden-file-input"
              />
              <button
                type="button"
                className={`avatar-picker ${uploadingImage ? 'disabled' : ''}`}
                onClick={openImagePicker}
                disabled={uploadingImage}
                aria-label="Upload profile image"
              >
                {form.profile_image_url ? (
                  <img src={getAbsoluteImageUrl(form.profile_image_url)} alt="Selected profile" />
                ) : (
                  <span className="avatar-plus">+</span>
                )}
              </button>
              {uploadingImage ? <div className="hint">Uploading image...</div> : null}
            </div>
            <div className="form-row">
              <input
                type="text"
                placeholder="Name"
                value={form.parental_name}
                onChange={(e) => setForm((prev) => ({ ...prev, parental_name: e.target.value }))}
                maxLength={40}
              />
            </div>
            <div className="form-row">
              <input
                type="password"
                placeholder="Parental PIN"
                value={form.parental_pin}
                onChange={(e) => setForm((prev) => ({ ...prev, parental_pin: e.target.value }))}
                maxLength={6}
              />
            </div>
            <div className="form-row actions">
              <button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Create Profile'}
              </button>
              <button type="button" className="secondary" onClick={goHome}>
                Continue
              </button>
            </div>
          </form>
        )}
        {pinModalOpen ? (
          <div className="profile-pin-backdrop">
            <div className="profile-pin-dialog">
              <h3>Enter Parental PIN</h3>
              <input
                type="password"
                maxLength={6}
                value={enteredPin}
                onChange={(e) => setEnteredPin(e.target.value)}
                placeholder="PIN"
              />
              <div className="profile-pin-dialog-actions">
                <button type="button" className="secondary" onClick={() => setPinModalOpen(false)}>Cancel</button>
                <button type="button" onClick={submitPinModal}>Continue</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default Profiles;
